import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { EditorEntityData } from '../../stores/editorStore';
import { useEditorStore } from '../../stores/editorStore';
import { normalizeAssetKey, resolveEntityImage } from '../../utils/assetPreview';

const LEVEL_IDS = ['level1', 'level2', 'level3', 'level4', 'level5', 'level6', 'level7'];

type EntitiesFile = {
  entities?: EditorEntityData[];
  additions?: {
    entities?: EditorEntityData[];
  };
};

type EntityInstanceCatalogItem = {
  instanceKey: string;
  editorEntityId?: string;
  entityKey: string;
  levelId: string;
  data: EditorEntityData;
};

type EntityTypeCatalogItem = {
  key: string;
  sample: EditorEntityData;
  instances: EntityInstanceCatalogItem[];
};

type AnimationEntry = {
  key: string;
  frames: string[];
  frameRate: number;
  repeat: number;
};

type AnimationsData = {
  entities: Record<string, { texture: string; animations: AnimationEntry[] }>;
};

type AtlasData = {
  frames: Record<string, unknown>;
};

type LevelEntitySource = {
  data: EditorEntityData;
  editorEntityId?: string;
};

async function parseJsonSafe<T>(response: Response): Promise<T | null> {
  if (!response.ok) return null;
  const raw = await response.text();
  const trimmed = raw.trim();
  if (!trimmed || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) return null;

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return null;
  }
}

function getEntityKey(entity: EditorEntityData): string {
  return normalizeAssetKey(entity.texture || entity.entityId || '');
}

function getInstanceKey(levelId: string, entity: EditorEntityData, index: number): string {
  const explicitId = (entity as { id?: string }).id;
  return explicitId ? `${levelId}:${explicitId}` : `${levelId}:${getEntityKey(entity)}:${index}`;
}

function getEntityIdLabel(instance: EntityInstanceCatalogItem): string {
  const explicitId = (instance.data as { id?: string | number }).id;
  if (explicitId !== undefined && explicitId !== null && explicitId !== '') {
    return String(explicitId);
  }

  return instance.instanceKey.split(':').at(-1) ?? instance.instanceKey;
}

function deriveStateName(frameName: string, entityKey: string): string {
  const normalizedEntity = normalizeAssetKey(entityKey);
  const normalizedFrame = frameName.toLowerCase();
  const withoutEntityPrefix = normalizedFrame.startsWith(`${normalizedEntity}_`)
    ? normalizedFrame.slice(normalizedEntity.length + 1)
    : normalizedFrame;

  return withoutEntityPrefix.replace(/_\d+$/, '');
}

function isScalar(value: unknown): boolean {
  return ['string', 'number', 'boolean'].includes(typeof value) || value === null;
}

function formatValue(value: unknown): string {
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  if (value === null) return 'null';
  if (Array.isArray(value)) return `${value.length} items`;
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parsePropertyValue(rawValue: string, currentValue: unknown): unknown {
  if (typeof currentValue === 'number') {
    const numericValue = parseFloat(rawValue);
    return Number.isNaN(numericValue) ? currentValue : numericValue;
  }

  if (typeof currentValue === 'boolean') {
    return rawValue.trim().toLowerCase() === 'true';
  }

  if (currentValue === null && rawValue.trim().toLowerCase() === 'null') {
    return null;
  }

  return rawValue;
}

function collectPropertyRows(entity: EditorEntityData): Array<{ label: string; value: unknown }> {
  const rows: Array<{ label: string; value: unknown }> = [];
  const properties = entity.properties;
  if (properties && typeof properties === 'object') {
    for (const [key, value] of Object.entries(properties)) {
      if (value !== undefined && isScalar(value)) {
        rows.push({ label: key, value });
      }
    }
  }

  return rows;
}

export default function AllEntitiesView() {
  const currentLevelId = useEditorStore((state) => state.levelId);
  const currentLevelEntities = useEditorStore((state) => state.entities);
  const [types, setTypes] = useState<EntityTypeCatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTypeKey, setSelectedTypeKey] = useState<string | null>(null);
  const [selectedInstanceKey, setSelectedInstanceKey] = useState<string | null>(null);
  const [states, setStates] = useState<string[]>([]);
  const [localPropertyValues, setLocalPropertyValues] = useState<Record<string, string>>({});
  const updateEntityInstanceProperty = useEditorStore((state) => state.updateEntityInstanceProperty);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      setIsLoading(true);
      try {
        const entries = await Promise.all(
          LEVEL_IDS.map(async (levelId) => {
            const [baseFile, overridesFile] = await Promise.all([
              fetch(`/assets/levels/${levelId}-entities-c3data.json`).then((response) =>
                parseJsonSafe<EntitiesFile>(response)
              ),
              fetch(`/assets/levels/${levelId}-entities-overrides.json`).then((response) =>
                parseJsonSafe<EntitiesFile>(response)
              ),
            ]);

            const entities: LevelEntitySource[] =
              currentLevelId === levelId && currentLevelEntities.length > 0
                ? currentLevelEntities.map((entity) => ({
                    data: entity.data,
                    editorEntityId: entity.id,
                  }))
                : [
                    ...(baseFile?.entities ?? []),
                    ...(overridesFile?.entities ?? []),
                    ...(overridesFile?.additions?.entities ?? []),
                  ].map((entity) => ({ data: entity }));

            return { levelId, entities };
          })
        );

        const byType = new Map<string, EntityTypeCatalogItem>();
        for (const entry of entries) {
          entry.entities.forEach((source, index) => {
            const entity = source.data;
            const entityKey = getEntityKey(entity);
            if (!entityKey) return;

            const instance: EntityInstanceCatalogItem = {
              instanceKey: getInstanceKey(entry.levelId, entity, index),
              editorEntityId: source.editorEntityId,
              entityKey,
              levelId: entry.levelId,
              data: entity,
            };

            const existing = byType.get(entityKey);
            if (existing) {
              existing.instances.push(instance);
              return;
            }

            byType.set(entityKey, {
              key: entityKey,
              sample: entity,
              instances: [instance],
            });
          });
        }

        if (!cancelled) {
          const nextTypes = Array.from(byType.values()).sort((a, b) => a.key.localeCompare(b.key));
          setTypes(nextTypes);
          setSelectedTypeKey((current) => current ?? nextTypes[0]?.key ?? null);
          setSelectedInstanceKey((current) =>
            current && nextTypes.some((type) => type.instances.some((instance) => instance.instanceKey === current))
              ? current
              : null
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, [currentLevelEntities, currentLevelId]);

  const filteredTypes = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return types;
    return types.filter(
      (type) =>
        type.key.includes(query) ||
        type.sample.entityId.toLowerCase().includes(query) ||
        type.instances.some((instance) => instance.levelId.includes(query))
    );
  }, [search, types]);

  const selectedType =
    filteredTypes.find((type) => type.key === selectedTypeKey) ?? filteredTypes[0] ?? null;
  const groupedInstances = useMemo(() => {
    const groups = new Map<string, EntityInstanceCatalogItem[]>();
    for (const instance of selectedType?.instances ?? []) {
      const existing = groups.get(instance.levelId) ?? [];
      existing.push(instance);
      groups.set(instance.levelId, existing);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [selectedType]);
  const selectedInstance =
    selectedType?.instances.find((instance) => instance.instanceKey === selectedInstanceKey) ?? null;
  const selectedInstanceProperties = selectedInstance ? collectPropertyRows(selectedInstance.data) : [];

  useEffect(() => {
    setLocalPropertyValues(
      Object.fromEntries(selectedInstanceProperties.map((row) => [row.label, formatValue(row.value)]))
    );
  }, [selectedInstanceKey]);

  const handlePropertyChange = (prop: string, value: string) => {
    setLocalPropertyValues((current) => ({ ...current, [prop]: value }));
  };

  const handlePropertyCommit = (prop: string, currentValue: unknown) => {
    if (!selectedInstance) return;
    const rawValue = localPropertyValues[prop] ?? '';
    const nextValue = parsePropertyValue(rawValue, currentValue);

    setTypes((currentTypes) =>
      currentTypes.map((type) => ({
        ...type,
        sample:
          type.sample === selectedInstance.data
            ? {
                ...type.sample,
                properties: {
                  ...(type.sample.properties ?? {}),
                  [prop]: nextValue,
                },
              }
            : type.sample,
        instances: type.instances.map((instance) =>
          instance.instanceKey === selectedInstance.instanceKey
            ? {
                ...instance,
                data: {
                  ...instance.data,
                  properties: {
                    ...(instance.data.properties ?? {}),
                    [prop]: nextValue,
                  },
                },
              }
            : instance
        ),
      }))
    );

    if (selectedInstance.editorEntityId) {
      updateEntityInstanceProperty(selectedInstance.editorEntityId, prop, nextValue);
    }
  };

  const handlePropertyKeyDown = (event: KeyboardEvent<HTMLInputElement>, prop: string, currentValue: unknown) => {
    if (event.key !== 'Enter') return;
    handlePropertyCommit(prop, currentValue);
    event.currentTarget.blur();
  };

  useEffect(() => {
    if (!selectedType) {
      setStates([]);
      return;
    }

    let cancelled = false;

    const loadStates = async () => {
      const normalizedKey = normalizeAssetKey(selectedType.key);
      const animationsData = await fetch('/assets/data/animations-c3data.json')
        .then((response) => parseJsonSafe<AnimationsData>(response))
        .catch(() => null);

      const matchingAnimations = Object.entries(animationsData?.entities ?? {}).flatMap(
        ([entityKey, data]) => {
          const matchesEntity = normalizeAssetKey(entityKey) === normalizedKey;
          const matchesTexture = normalizeAssetKey(data.texture) === normalizedKey;
          return matchesEntity || matchesTexture ? data.animations.map((animation) => animation.key) : [];
        }
      );

      if (matchingAnimations.length > 0) {
        if (!cancelled) setStates(Array.from(new Set(matchingAnimations)).sort());
        return;
      }

      const atlas = await fetch(`/assets/game/spritesheets/${normalizedKey}.json`)
        .then((response) => parseJsonSafe<AtlasData>(response))
        .catch(() => null);
      const frameStates = Object.keys(atlas?.frames ?? {}).map((frameName) =>
        deriveStateName(frameName, selectedType.key)
      );

      if (!cancelled) setStates(Array.from(new Set(frameStates)).filter(Boolean).sort());
    };

    loadStates();

    return () => {
      cancelled = true;
    };
  }, [selectedType]);

  return (
    <div className="all-entities-view">
      <div className="all-entities-window">
        <div className="all-entities-toolbar">
          <div>
            <h2>All Entities</h2>
            <span>
              {isLoading
                ? 'Loading entities...'
                : `${filteredTypes.length} entity types | ${selectedType?.instances.length ?? 0} copies selected`}
            </span>
          </div>
          <input
            className="all-entities-search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setSelectedInstanceKey(null);
            }}
            placeholder="Search entities"
          />
        </div>

        <div className="all-entities-body">
          <section className="all-entities-main">
            {isLoading ? (
              <div className="all-entities-empty">Loading entities...</div>
            ) : filteredTypes.length === 0 ? (
              <div className="all-entities-empty">No entities found</div>
            ) : (
              <>
                <div className="all-entities-grid">
                  {filteredTypes.map((item) => (
                    <button
                      key={item.key}
                      className={`all-entity-card ${selectedType?.key === item.key ? 'active' : ''}`}
                      type="button"
                      title={item.key}
                      onClick={() => {
                        setSelectedTypeKey(item.key);
                        setSelectedInstanceKey(null);
                      }}
                    >
                      <div className="all-entity-thumb-wrap">
                        <img
                          src={resolveEntityImage(item.key)}
                          alt={item.key}
                          className="all-entity-thumb"
                          loading="lazy"
                          draggable={false}
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                      <span className="all-entity-name">{item.key}</span>
                      <span className="all-entity-meta">{item.instances.length} copias</span>
                    </button>
                  ))}
                </div>

                <div className="all-entity-map-copies">
                  <div className="all-entity-section-title">
                    <h3>Copias por mapa</h3>
                    <span>{selectedType?.key}</span>
                  </div>
                  <div className="all-entity-map-copy-list">
                    {groupedInstances.map(([levelId, instances]) => (
                      <div className="all-entity-map-group" key={levelId}>
                        <div className="all-entity-map-title">
                          <strong>{levelId}</strong>
                          <span>{instances.length}</span>
                        </div>
                        <div className="all-entity-copy-grid">
                          {instances.map((instance, copyIndex) => (
                            <button
                              key={instance.instanceKey}
                              type="button"
                              className={`all-entity-copy-card ${
                                selectedInstance?.instanceKey === instance.instanceKey ? 'active' : ''
                              }`}
                              onClick={() => setSelectedInstanceKey(instance.instanceKey)}
                            >
                              <img
                                src={resolveEntityImage(instance.entityKey)}
                                alt={instance.entityKey}
                                draggable={false}
                                onError={(event) => {
                                  event.currentTarget.style.display = 'none';
                                }}
                              />
                              <span className="all-entity-copy-id">ID {getEntityIdLabel(instance)}</span>
                              <span className="all-entity-copy-number">Copia #{copyIndex + 1}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>

          <aside className="all-entities-detail">
            <div className="all-entities-info-title">INFO</div>
            {selectedInstance ? (
              <>
              <div className="all-entities-detail-header">
                <img
                  src={resolveEntityImage(selectedInstance.entityKey)}
                  alt={selectedInstance.entityKey}
                  draggable={false}
                />
                <div>
                  <h3>{selectedInstance.entityKey}</h3>
                  <span>{selectedInstance.levelId}</span>
                </div>
              </div>

              <section className="all-entities-detail-section">
                <h4>Estados</h4>
                {states.length > 0 ? (
                  <div className="all-entity-state-list compact">
                    {states.map((state) => (
                      <span key={state}>{state}</span>
                    ))}
                  </div>
                ) : (
                  <p>No se encontraron estados.</p>
                )}
              </section>

              <section className="all-entities-detail-section">
                <h4>Propiedades</h4>
                <div className="all-entity-attribute-list">
                  {selectedInstanceProperties.map((row) => (
                    <div className="all-entity-attribute-row" key={row.label}>
                      <span>{row.label}</span>
                      <input
                        className="all-entity-property-input"
                        type={typeof row.value === 'number' ? 'number' : 'text'}
                        value={localPropertyValues[row.label] ?? ''}
                        onChange={(event) => handlePropertyChange(row.label, event.target.value)}
                        onBlur={() => handlePropertyCommit(row.label, row.value)}
                        onKeyDown={(event) => handlePropertyKeyDown(event, row.label, row.value)}
                      />
                    </div>
                  ))}
                  {selectedInstanceProperties.length === 0 && <p>No se encontraron propiedades.</p>}
                </div>
              </section>
              </>
            ) : (
              <>
                <div className="all-entities-detail-header">
                  {selectedType && (
                    <img
                      src={resolveEntityImage(selectedType.key)}
                      alt={selectedType.key}
                      draggable={false}
                    />
                  )}
                  <div>
                    <h3>{selectedType?.key ?? 'Sin entidad'}</h3>
                    <span>Selecciona una copia</span>
                  </div>
                </div>

                <section className="all-entities-detail-section">
                  <h4>Estados</h4>
                  {states.length > 0 ? (
                    <div className="all-entity-state-list compact">
                      {states.map((state) => (
                        <span key={state}>{state}</span>
                      ))}
                    </div>
                  ) : (
                    <p>No se encontraron estados.</p>
                  )}
                </section>

                <section className="all-entities-detail-section">
                  <h4>Propiedades</h4>
                  <p>Selecciona una copia del mapa para ver sus datos.</p>
                </section>
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
